import { faPalette, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { Button } from '../../../../components/forms/Button';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import { ColorRuleType, FieldConfiguration } from '../../../types';
import { ColorButton } from './ColorButton';

interface ColorConfiguratorProps {
	field: FieldConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	isModalOpen?: boolean;
}

export function ColorConfigurator({
	field,
	onUpdateField,
	isModalOpen = false,
}: ColorConfiguratorProps) {
	const colors = useThemeColors();

	const renderColorRuleConfiguration = () => {
		const { colorRule } = field.styleConfig;

		switch (colorRule.type) {
			case 'default':
				return null; // No configuration needed for default

			case 'static':
				return (
					<div>
						<div className="flex gap-4">
							<div className="flex-1">
								<ColorButton
									label={
										field.styleConfig.variant === 'outline'
											? 'Outline'
											: 'Background'
									}
									color={colorRule.staticColor?.bg || colors.values.primary}
									onChange={(color) => {
										onUpdateField({
											styleConfig: {
												...field.styleConfig,
												colorRule: {
													...colorRule,
													staticColor: {
														...colorRule.staticColor,
														bg: color,
													},
												},
											},
										});
									}}
									isModalOpen={isModalOpen}
								/>
							</div>
							<div className="flex-1">
								<ColorButton
									label="Text Color"
									color={colorRule.staticColor?.text || colors.values.white}
									onChange={(color) => {
										onUpdateField({
											styleConfig: {
												...field.styleConfig,
												colorRule: {
													...colorRule,
													staticColor: {
														...colorRule.staticColor,
														text: color,
													},
												},
											},
										});
									}}
									isModalOpen={isModalOpen}
								/>
							</div>
						</div>
					</div>
				);

			case 'range-mapping':
				return (
					<div>
						<Text className="block text-xs mb-3" color="secondary">
							Map numeric ranges to colors
						</Text>

						{/* Range Mappings */}
						{(colorRule.rangeMapping || []).map((range, index) => {
							return (
								<div
									key={`range_${index}`}
									className={cn(
										'mb-3 p-3 rounded-md',
										colors.classes.bgSecondary,
									)}
								>
									<div className="flex items-center mb-2 gap-2">
										<Input
											type="text"
											inputMode="numeric"
											value={String(range.min)}
											placeholder="Min"
											className="flex-1 text-xs"
											size="sm"
											onChange={(e) => {
												const val = e.target.value;
												// Allow typing negative numbers and partial input
												if (val === '' || val === '-' || !isNaN(Number(val))) {
													const newRanges = [...(colorRule.rangeMapping || [])];
													newRanges[index] = {
														...range,
														min:
															val === '' || val === '-'
																? (val as unknown as number)
																: Number(val),
													};
													onUpdateField({
														styleConfig: {
															...field.styleConfig,
															colorRule: {
																...colorRule,
																rangeMapping: newRanges,
															},
														},
													});
												}
											}}
											onBlur={(e) => {
												// Finalize value on blur
												const num = Number(e.target.value);
												if (isNaN(num)) {
													const newRanges = [...(colorRule.rangeMapping || [])];
													newRanges[index] = { ...range, min: 0 };
													onUpdateField({
														styleConfig: {
															...field.styleConfig,
															colorRule: {
																...colorRule,
																rangeMapping: newRanges,
															},
														},
													});
												}
											}}
										/>
										<Text className="text-xs" color="secondary">
											to
										</Text>
										<Input
											type="text"
											inputMode="numeric"
											value={String(range.max)}
											placeholder="Max"
											className="flex-1 text-xs"
											size="sm"
											onChange={(e) => {
												const val = e.target.value;
												if (val === '' || val === '-' || !isNaN(Number(val))) {
													const newRanges = [...(colorRule.rangeMapping || [])];
													newRanges[index] = {
														...range,
														max:
															val === '' || val === '-'
																? (val as unknown as number)
																: Number(val),
													};
													onUpdateField({
														styleConfig: {
															...field.styleConfig,
															colorRule: {
																...colorRule,
																rangeMapping: newRanges,
															},
														},
													});
												}
											}}
											onBlur={(e) => {
												const num = Number(e.target.value);
												if (isNaN(num)) {
													const newRanges = [...(colorRule.rangeMapping || [])];
													newRanges[index] = { ...range, max: 0 };
													onUpdateField({
														styleConfig: {
															...field.styleConfig,
															colorRule: {
																...colorRule,
																rangeMapping: newRanges,
															},
														},
													});
												}
											}}
										/>
										<button
											onClick={() => {
												const newRanges = (colorRule.rangeMapping || []).filter(
													(_, i) => i !== index,
												);
												onUpdateField({
													styleConfig: {
														...field.styleConfig,
														colorRule: {
															...colorRule,
															rangeMapping: newRanges,
														},
													},
												});
											}}
											className={cn(
												'p-1 rounded',
												colors.classes.bgPrimaryHover,
											)}
										>
											<Icon
												icon={faTrash}
												size="sm"
												className={colors.classes.textSecondary}
											/>
										</button>
									</div>

									<div className="flex gap-2">
										<div className="flex-1">
											<ColorButton
												label={
													field.styleConfig.variant === 'outline'
														? 'Outline'
														: 'Background'
												}
												color={range.color.bg ?? ''}
												onChange={(color) => {
													const newRanges = [...(colorRule.rangeMapping || [])];
													newRanges[index] = {
														...range,
														color: { ...range.color, bg: color },
													};
													onUpdateField({
														styleConfig: {
															...field.styleConfig,
															colorRule: {
																...colorRule,
																rangeMapping: newRanges,
															},
														},
													});
												}}
												isModalOpen={isModalOpen}
											/>
										</div>
										<div className="flex-1">
											<ColorButton
												label="Text"
												color={range.color.text ?? ''}
												onChange={(color) => {
													const newRanges = [...(colorRule.rangeMapping || [])];
													newRanges[index] = {
														...range,
														color: { ...range.color, text: color },
													};
													onUpdateField({
														styleConfig: {
															...field.styleConfig,
															colorRule: {
																...colorRule,
																rangeMapping: newRanges,
															},
														},
													});
												}}
												isModalOpen={isModalOpen}
											/>
										</div>
									</div>
								</div>
							);
						})}

						{/* Add Range Button */}
						<Button
							onClick={() => {
								const newRanges = [...(colorRule.rangeMapping || [])];
								const lastRange = newRanges[newRanges.length - 1];
								const newMin = lastRange ? lastRange.max : 0;
								newRanges.push({
									min: newMin,
									max: newMin + 100,
									color: {
										bg: '',
										text: 'default-text',
									},
								});
								onUpdateField({
									styleConfig: {
										...field.styleConfig,
										colorRule: {
											...colorRule,
											rangeMapping: newRanges,
										},
									},
								});
							}}
							size="sm"
							variant="primary"
							icon={faPlus}
						>
							Add Range
						</Button>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<Card className="mb-4">
			<div className="p-4">
				<div className="flex items-center mb-3">
					<Icon
						icon={faPalette}
						size="sm"
						color={colors.values.textPrimary}
						className="mr-2"
					/>
					<Text className="text-base font-semibold">Color Settings</Text>
				</div>

				{/* Color Rule Type Selection */}
				<div className="mb-3">
					<Text className="text-sm font-medium mb-2">Color Rule Type</Text>
					<Select
						value={field.styleConfig.colorRule.type}
						options={[
							{
								value: 'default' as ColorRuleType,
								label: 'Default (No color change)',
							},
							{ value: 'theme' as ColorRuleType, label: 'Secondary Text' },
							{ value: 'static' as ColorRuleType, label: 'Single Color' },
							{
								value: 'range-mapping' as ColorRuleType,
								label: 'Range-based Colors',
							},
						]}
						onChange={(value) => {
							onUpdateField({
								styleConfig: {
									...field.styleConfig,
									colorRule: {
										type: value as ColorRuleType,
										...(value === 'theme'
											? { themeColor: 'textSecondary' }
											: {}),
										...(value === 'static'
											? {
													staticColor: {
														bg: colors.values.primary,
														text: colors.values.white,
													},
												}
											: {}),
										...(value === 'range-mapping' ? { rangeMapping: [] } : {}),
									},
								},
							});
						}}
					/>
				</div>

				{/* Color Rule Configuration */}
				{renderColorRuleConfiguration()}
			</div>
		</Card>
	);
}
